import type React from 'react'

export interface StrikeChipProps {
    strike: number
    label?: string | null
    type?: 'support' | 'resistance' | 'atm' | 'neutral'
    isAtm?: boolean
    className?: string
    /** Direction arrow relative to Top1: 'up' = higher strike, 'down' = lower strike */
    direction?: 'up' | 'down' | null
}

export const StrikeChip: React.FC<StrikeChipProps> = ({ strike, label, type = 'neutral', isAtm, className, direction }) => {
    const baseClasses =
        'relative w-full max-w-[110px] h-9 flex items-center justify-center rounded-lg text-[15px] font-black tracking-tight transition-all duration-300 mx-auto'

    // Container border + bg (neutral default)
    let containerClasses = 'bg-linear-to-tr from-white/40 to-slate-50/10 dark:from-slate-700/40 dark:to-slate-800/10 backdrop-blur-xl ring-1 ring-inset ring-slate-200/50 dark:ring-white/10 text-slate-900 dark:text-white shadow-[0_2px_8px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.2),inset_0_1px_1px_rgba(255,255,255,0.1)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:-translate-y-px'
    if (isAtm) {
        containerClasses = 'bg-linear-to-tr from-yellow-100/50 to-yellow-50/10 dark:from-yellow-500/20 dark:to-yellow-500/5 backdrop-blur-xl ring-1 ring-inset ring-yellow-400/50 dark:ring-yellow-500/30 text-yellow-900 dark:text-yellow-200 shadow-[0_4px_12px_rgba(251,191,36,0.15),inset_0_1px_1px_rgba(255,255,255,0.8)] dark:shadow-[0_4px_12px_rgba(251,191,36,0.1),inset_0_1px_1px_rgba(255,255,255,0.1)] hover:shadow-[0_6px_16px_rgba(251,191,36,0.2)] hover:-translate-y-px'
    }

    // Parse Labels
    const labels = label ? label.split(',').map((l) => l.trim()) : []

    const getBadgeStyle = (l: string) => {
        if (l === 'CE') return 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300/60 text-emerald-600 dark:text-emerald-400'
        if (l === 'PE') return 'bg-rose-50 dark:bg-rose-900/30 border border-rose-300/60 text-rose-600 dark:text-rose-400'
        if (l === 'T1') return 'bg-amber-400 border border-amber-500 text-white'
        if (l === 'T2') return 'bg-blue-400 border border-blue-500 text-white'
        if (l.includes('Sh'))
            return 'bg-white dark:bg-slate-900 border border-indigo-300/60 text-indigo-600 dark:text-indigo-400'
        if (l.includes('S'))
            return 'bg-white dark:bg-slate-900 border border-emerald-300/60 text-emerald-600 dark:text-emerald-400'
        if (l.includes('R'))
            return 'bg-white dark:bg-slate-900 border border-rose-300/60 text-rose-600 dark:text-rose-400'
        return 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300/60 text-yellow-700 dark:text-yellow-400'
    }

    return (
        <div className={`${baseClasses} ${containerClasses} ${className || ''}`.trim()}>
            {/* Direction arrow */}
            {direction === 'up' && (
                <span className="text-[10px] mr-0.5 opacity-90 text-emerald-500 dark:text-emerald-400">▲</span>
            )}
            {direction === 'down' && (
                <span className="text-[10px] mr-0.5 opacity-90 text-rose-500 dark:text-rose-400">▼</span>
            )}
            {strike}
            {labels.length > 0 && (
                <div className="absolute -top-2 -right-1.5 flex flex-row items-center gap-0.5 z-20">
                    {labels.map((l, i) => (
                        <div
                            key={i}
                            className={`h-[16px] min-w-[18px] px-1 rounded-full flex items-center justify-center font-black text-[8px] ring-1 ring-white dark:ring-slate-950 ${getBadgeStyle(l)}`}
                        >
                            {l}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
